import Crypto from 'crypto';

import Cookies from 'js-cookie';

import type { AppData } from '../../types/app_data';

import type { AnalyticsUser } from './types';

const formatPhone = (phone?: string): string => {
  if (!phone) return '';
  return phone?.startsWith?.('+1') ? phone : `+1${phone}`;
};

const extractUserID = (userID: string | undefined): string | undefined => {
  if (!userID) return undefined;
  const decoded = atob(userID);
  const parts = decoded.split(':');
  return parts.length > 1 ? parts[1] : undefined;
};

/**
 * Helper to strip leading and trailing whitespace
 * from the value param and convert it to lowercase and
 * return the hexdigest of sha256 hash
 * @param value
 */
const hash = (plaintext: string): string =>
  Crypto.createHash('sha256').update(plaintext.trim().toLowerCase(), 'utf8').digest('hex');

/**
 * Identify user to Tiktok Pixel.
 * https://business-api.tiktok.com/portal/docs?id=1771101151059969
 *
 * @param user
 */
export const setUserForTikTokPixel = async (user: AnalyticsUser): Promise<void> => {
  if (window.ttq) {
    const userID = user.id ? extractUserID(user.id) : undefined;
    window.ttq.identify({
      limited_data_use: user.enableLDU ? true : undefined,
      external_id: userID ? hash(userID) : undefined,
      phone: user.phone ? hash(formatPhone(user.phone)) : undefined,
      email: user.email ? hash(user.email.trim()) : undefined,
      ttp: Cookies.get('_ttp') || undefined,
    });
  }
};

/**
 * Track signup conversion.
 * https://business-api.tiktok.com/portal/docs?id=1771101151059969
 */
export const trackSignedUpForTikTok = (appData?: AppData): void => {
  if (window.ttq) {
    const userID = appData?.id ? extractUserID(appData.id) : undefined;
    window.ttq.track('CompleteRegistration', {
      limited_data_use: appData?.enableLDU ? true : undefined,
      external_id: userID ? hash(userID) : undefined,
      phone: appData?.phone ? hash(formatPhone(appData.phone)) : undefined,
      email: appData?.email ? hash(appData.email.trim()) : undefined,
      ttp: Cookies.get('_ttp') || undefined,
    });
  }
};

/**
 * Track onboarded conversion.
 * https://business-api.tiktok.com/portal/docs?id=1771101151059969
 */
export const trackOnboardedForTikTok = (appData?: AppData): void => {
  if (window.ttq) {
    const userID = appData?.id ? extractUserID(appData.id) : undefined;
    window.ttq.track('ViewContent', {
      limited_data_use: appData?.enableLDU ? true : undefined,
      external_id: userID ? hash(userID) : undefined,
      phone: appData?.phone ? hash(formatPhone(appData.phone)) : undefined,
      email: appData?.email ? hash(appData.email.trim()) : undefined,
      ttp: Cookies.get('_ttp') || undefined,
    });
  }
};
